import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { isAxiosError } from 'axios'
import { clamp } from 'lodash'
import { useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useGetResidentVehicles, useUpdateResidentVehicles } from '@/api/parking-management'
import { TutorialModal } from '@/components/layout/modals/tutorial-modal'
import { TutorialBanner } from '@/components/layout/tutorial-banner'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils'

import { MAX_VEHICLES } from './parking-modals.constants'
import * as S from './parking-modals.styled'
import { makeResidentVehiclesFormSchema, ResidentVehiclesFormData } from './parking-modals.types'
import {
  buildVehicleDetails,
  formatParkingSpots,
  getEmptyVehicle,
  isDuplicatedLicensePlatesError,
  isOccupiedLicensePlateError,
  isVehicleApiError,
  mapApiVehicleToFormData,
  openAssociateVehiclePopup,
  parseCanAssociateError,
  trimEmptyVehicles,
} from './parking-modals.utils'
import { VehiclesForm } from './vehicles-form'

type ResidentVehiclesModalProps = {
  residentId: number
  onClose: () => void
}

export const ResidentVehiclesModal = ({ residentId, onClose }: ResidentVehiclesModalProps) => {
  const { t } = useTranslation(['parking-management'])
  const [activeVehicleIndex, setActiveVehicleIndex] = useState(0)

  const { data: residentVehiclesData, isLoading } = useGetResidentVehicles({ residentId })
  const { isPending: isUpdatePending, mutateAsync: updateResidentVehicles } =
    useUpdateResidentVehicles()

  const schema = useMemo(() => makeResidentVehiclesFormSchema(t), [t])

  const form = useForm<ResidentVehiclesFormData>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    values: {
      vehicles: residentVehiclesData?.vehicles.length
        ? residentVehiclesData.vehicles.map(mapApiVehicleToFormData)
        : [getEmptyVehicle()],
    },
  })

  const {
    fields: vehicleFields,
    append,
    replace,
  } = useFieldArray({
    control: form.control,
    keyName: 'vehicleId',
    name: 'vehicles',
  })

  const buildVehiclePayload = (formData: ResidentVehiclesFormData) =>
    trimEmptyVehicles(formData.vehicles).map((vehicle) => ({
      color: vehicle.color,
      id: vehicle.id,
      licensePlate: vehicle.licensePlate,
      make: vehicle.make,
      model: vehicle.model,
      permitTag: vehicle.permitTag,
      year: vehicle.year ? Number(vehicle.year) : undefined,
    }))

  const handleSubmit = async (formData: ResidentVehiclesFormData) => {
    try {
      await updateResidentVehicles({
        clientId: residentId,
        vehicles: buildVehiclePayload(formData),
      })
      toastFactory.success(t('parking-management:residentVehiclesModal.toastUpdateSuccess'))
      onClose()
    } catch (error) {
      const errorResponse = isAxiosError(error) ? error.response?.data : undefined

      if (parseCanAssociateError(errorResponse)) {
        const matchingVehicle =
          formData.vehicles.find((vehicle) => !vehicle.id) ?? formData.vehicles[0]
        openAssociateVehiclePopup(buildVehicleDetails(matchingVehicle), t, async () => {
          await updateResidentVehicles({
            clientId: residentId,
            confirmAssociation: true,
            vehicles: buildVehiclePayload(formData),
          })
          toastFactory.success(t('parking-management:residentVehiclesModal.toastAssociateSuccess'))
          onClose()
        })
        return
      }

      if (isDuplicatedLicensePlatesError(errorResponse)) {
        const duplicatedPlates = new Set(
          errorResponse.duplicatedLicensePlates.map((plate) => plate.toLowerCase()),
        )
        formData.vehicles.forEach((vehicle, index) => {
          if (duplicatedPlates.has(vehicle.licensePlate.toLowerCase())) {
            form.setError(`vehicles.${index}.licensePlate`, {
              message: t('parking-management:residentVehiclesModal.duplicatedLicensePlateError'),
              type: 'validate',
            })
          }
        })
        return
      }

      if (isOccupiedLicensePlateError(errorResponse)) {
        formData.vehicles.forEach((vehicle, index) => {
          if (!vehicle.id) {
            form.setError(`vehicles.${index}.licensePlate`, {
              message: errorResponse.message,
              type: 'validate',
            })
          }
        })
        return
      }

      if (!isVehicleApiError(errorResponse)) {
        toastFactory.error(getApiErrorMessage(error))
      }
    }
  }

  const handleAddVehicle = async () => {
    const isValid = await form.trigger('vehicles')
    if (!isValid) return
    append(getEmptyVehicle())
    setActiveVehicleIndex(vehicleFields.length)
  }

  const handleRemoveVehicle = () => {
    popupFactory.open(
      ConfirmationPopup,
      {
        children: t('parking-management:residentVehiclesModal.removeVehiclePopup.body'),
        confirmText: (
          <>
            <Icon icon={TrashIcon} />{' '}
            {t('parking-management:residentVehiclesModal.removeVehiclePopup.confirm')}
          </>
        ),
        onConfirm: async () => {
          if (vehicleFields.length <= 1) {
            replace([getEmptyVehicle()])
            setActiveVehicleIndex(0)
          } else {
            const nextVehicles = form
              .getValues('vehicles')
              .filter((_, vehicleIndex) => vehicleIndex !== activeVehicleIndex)

            replace(nextVehicles)
            setActiveVehicleIndex((prev) => clamp(prev, 0, Math.max(0, nextVehicles.length - 1)))
          }
          if (form.formState.errors.vehicles) {
            await form.trigger('vehicles')
          }
        },
        title: t('parking-management:residentVehiclesModal.removeVehiclePopup.title'),
        variant: 'danger',
      },
      { suspendCurrent: true },
    )
  }

  const handleWatchTutorial = () => {
    popupFactory.open(
      TutorialModal,
      {
        video: {
          alt: t('parking-management:residentVehiclesModal.tutorialVideoAlt'),
          previewUrl: '/images/upselling/parking-management.png',
          url: 'https://redvike-chefs.s3.us-east-1.amazonaws.com/ParkingManagementUpsell.mp4',
        },
      },
      { suspendCurrent: true },
    )
  }

  const activeVehicle = form.watch(`vehicles.${activeVehicleIndex}`)
  const isActiveVehiclePersisted = Boolean(activeVehicle?.id)
  const vehicleErrors = form.formState.errors.vehicles
  const hasLicensePlateError =
    Array.isArray(vehicleErrors) && vehicleErrors.some((vehicle) => vehicle?.licensePlate)
  const isDisabled = isLoading || isUpdatePending
  const hasVehicleErrors = !!form.formState.errors.vehicles
  const shouldConfirmModalClose = form.formState.isDirty && !isUpdatePending

  const handleClose = () => {
    popupFactory.requestCloseWithConfirmation({
      modalInstance: ResidentVehiclesModal,
      onClose,
      shouldConfirm: shouldConfirmModalClose,
    })
    return false
  }

  return (
    <Modal
      autoWidth
      size="medium"
      title={t('parking-management:residentVehiclesModal.title')}
      closeOnOverlayClick={false}
      closeOnEscapeKey={false}
      footer={
        <>
          <S.AddCarButton
            disabled={isDisabled || hasVehicleErrors || vehicleFields.length >= MAX_VEHICLES}
            use="secondary"
            onClick={handleAddVehicle}
          >
            {t('parking-management:residentVehiclesModal.addCar')}
            <Icon icon={PlusIcon} />
          </S.AddCarButton>
          <Button disabled={isDisabled} use="secondary" onClick={handleClose}>
            {t('parking-management:residentVehiclesModal.cancel')}
          </Button>
          <Button
            disabled={isLoading || !form.formState.isDirty || hasLicensePlateError}
            loading={isUpdatePending}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t('parking-management:residentVehiclesModal.save')}
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Content>
          <TextInput
            disabled
            readOnly
            label={t('parking-management:residentVehiclesModal.spotLabel')}
            value={formatParkingSpots(
              residentVehiclesData && [
                ...new Set(residentVehiclesData.parkings.map((parking) => parking.parkingSpot)),
              ],
            )}
          />
          <TutorialBanner
            description={t('parking-management:residentVehiclesModal.tutorialDescription')}
            title={t('parking-management:residentVehiclesModal.tutorialTitle')}
            onButtonClick={handleWatchTutorial}
          />

          <VehiclesForm
            activeVehicleIndex={activeVehicleIndex}
            vehicleFields={vehicleFields}
            disabled={isDisabled}
            disabledLicensePlate={isActiveVehiclePersisted || isDisabled}
            onTabChange={setActiveVehicleIndex}
            onRemoveVehicle={handleRemoveVehicle}
          />
        </S.Content>
      </FormProvider>
    </Modal>
  )
}
